<template>
  <div class="ar-page">
      <div class="pg-title-row">
        <div>
          <h1 class="pg-title">All Runners</h1>
          <p class="pg-title-sub">Search, review and manage complete runner profile information</p>
        </div>
      </div>

      <div class="ar-toolbar">
        <div class="ar-toolbar-left">
          <button class="ar-filter-btn" type="button" @click="showBugseeFilterModal = true">
            <svg class="ar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filters
            <span v-if="feedbackFilterEnabled" class="ar-filter-dot"></span>
          </button>
          <span v-if="feedbackFilterEnabled" class="ar-filter-badge">Feedback enabled</span>
          <runner-field-visibility-control
            modal-name="allRunnersFieldVisibility"
            title="All Runners Fields"
            subtitle="Choose the runner fields shown on this page"
            :field-options="fieldOptions"
            :selected-fields="selectedFieldIds"
            @apply="applyFieldSelection"
            @reset="resetFieldSelection"
          />
          <div class="ar-view-switcher" aria-label="Runner view mode">
            <button
              class="ar-view-option"
              :class="{ 'ar-view-option--active': viewMode === 'cards' }"
              type="button"
              :disabled="viewMode === 'cards'"
              @click="setViewMode('cards')"
            >
              <svg class="ar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                <rect x="14" y="14" width="7" height="7" rx="1.5"/>
              </svg>
              Cards
            </button>
            <button
              class="ar-view-option"
              :class="{ 'ar-view-option--active': viewMode === 'table' }"
              type="button"
              :disabled="viewMode === 'table'"
              @click="setViewMode('table')"
            >
              <svg class="ar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 6h16"/>
                <path d="M4 12h16"/>
                <path d="M4 18h16"/>
                <path d="M8 6v12"/>
                <path d="M16 6v12"/>
              </svg>
              Table
            </button>
          </div>
        </div>

        <div class="ar-search-panel">
          <div class="ar-search-row">
            <input
              class="ar-search-input"
              type="text"
              placeholder="Search by name, location, email, promo code"
              v-model="search"
              @keyup.enter="handleSearch"
            />
            <button class="ar-primary-btn" type="button" @click="handleSearch">
              <svg class="ar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              Search
            </button>
          </div>
          <div class="ar-result-summary">
            {{ resultSummary }}
          </div>
        </div>
      </div>

      <div v-if="loading && viewMode === 'cards'" class="ar-list">
        <div v-for="n in 4" :key="'runner-skeleton-' + n" class="ar-card ar-card--skeleton">
          <div class="ar-card-header">
            <div class="ar-skeleton-icon sk-shimmer"></div>
            <div class="ar-skeleton-heading">
              <div class="ar-skeleton-title sk-shimmer"></div>
              <div class="ar-skeleton-subtitle sk-shimmer"></div>
            </div>
            <div class="ar-skeleton-actions">
              <div class="ar-skeleton-action sk-shimmer"></div>
              <div class="ar-skeleton-action sk-shimmer"></div>
              <div class="ar-skeleton-action sk-shimmer"></div>
            </div>
          </div>
          <div class="ar-skeleton-grid">
            <div v-for="field in 12" :key="'runner-skeleton-field-' + n + '-' + field" class="ar-skeleton-field sk-shimmer"></div>
          </div>
        </div>
      </div>

      <div v-else-if="loading" class="ar-table-list ar-table-list--skeleton">
        <div class="ar-table-header">
          <span>Runner</span>
          <span>Account</span>
          <span>Membership</span>
          <span>Profile</span>
          <span>Activity</span>
          <span>Actions</span>
        </div>
        <div v-for="n in 6" :key="'runner-table-skeleton-' + n" class="ar-table-row ar-table-row--skeleton">
          <div class="ar-table-main">
            <div class="ar-table-identity">
              <div class="ar-table-avatar sk-shimmer"></div>
              <div class="ar-table-identity-copy">
                <div class="ar-table-skeleton-title sk-shimmer"></div>
                <div class="ar-table-skeleton-meta sk-shimmer"></div>
              </div>
            </div>
            <div v-for="zone in 4" :key="'runner-table-zone-' + n + '-' + zone" class="ar-table-zone">
              <div class="ar-table-skeleton-label sk-shimmer"></div>
              <div class="ar-table-skeleton-value sk-shimmer"></div>
              <div class="ar-table-skeleton-value ar-table-skeleton-value--short sk-shimmer"></div>
            </div>
            <div class="ar-table-actions">
              <div class="ar-skeleton-action sk-shimmer"></div>
              <div class="ar-skeleton-action sk-shimmer"></div>
              <div class="ar-skeleton-action sk-shimmer"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="runners.length === 0" class="ar-empty">
        <span class="ar-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
            <path d="M8 11h6"/>
          </svg>
        </span>
        <h2 class="ar-empty-title">No runners found</h2>
        <p class="ar-empty-text">Try a different search term or adjust the feedback filter.</p>
      </div>

      <div v-else-if="viewMode === 'cards'" class="ar-list">
        <article
          v-for="(runner, index) in runners"
          :key="runner.id || index"
          class="ar-card ar-clickable-block"
          role="link"
          tabindex="0"
          :aria-label="'Open runner profile for ' + runnerName(runner)"
          @click="openRunnerProfile(runner)"
          @keydown.enter.prevent="openRunnerProfile(runner)"
          @keydown.space.prevent="openRunnerProfile(runner)"
        >
          <div class="ar-card-header">
            <span class="ar-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 21a8 8 0 0 1 16 0"/>
              </svg>
            </span>
            <div class="ar-runner-title">
              <h2 class="ar-runner-name">{{ runnerName(runner) }}</h2>
              <div class="ar-runner-meta">
                <button class="ar-id-link" type="button" @click.stop="getRunnersData(runner.id)">ID {{ displayValue(runner.id) }}</button>
                <template v-for="meta in runnerMetaFields(runner)">
                  <span :key="meta.id + '-dot'" class="ar-meta-dot"></span>
                  <span :key="meta.id">{{ meta.value }}</span>
                </template>
              </div>
            </div>
            <div class="ar-actions">
              <button class="ar-action-btn" type="button" @click.stop="getRunnersData(runner.id)" title="Open runner data">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 3h7v7"/>
                  <path d="M10 14L21 3"/>
                  <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/>
                </svg>
              </button>
              <button class="ar-action-btn" type="button" @click.stop="openPasswordModal(runner)" title="Change password">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </button>
              <button class="ar-action-btn" type="button" @click.stop="openBugseeSettingsModal(runner)" title="Feedback settings">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="ar-section-grid">
            <section v-if="isFieldGroupVisible('Account')" class="ar-info-section">
              <div class="ar-section-header">
                <span class="ar-section-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16v16H4z"/>
                    <path d="M4 9h16"/>
                    <path d="M9 20V9"/>
                  </svg>
                </span>
                <div>
                  <h3 class="ar-section-title">Account</h3>
                  <p class="ar-section-subtitle">Contact and sign-in details</p>
                </div>
              </div>
              <div class="ar-field-grid">
                <div v-if="isRunnerFieldVisible('email')" class="ar-field">
                  <span class="ar-label">Email</span>
                  <span class="ar-value ar-value--break">{{ runnerEmail(runner) }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('account-create')" class="ar-field">
                  <span class="ar-label">Account created</span>
                  <span class="ar-value">{{ runnerAccountCreate(runner) }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('facebook')" class="ar-field">
                  <span class="ar-label">Facebook</span>
                  <span class="ar-value">{{ runner.facebook ? 'Yes' : 'No' }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('website')" class="ar-field">
                  <span class="ar-label">Website</span>
                  <span class="ar-value ar-value--break">{{ displayValue(runner.website) }}</span>
                </div>
              </div>
            </section>

            <section v-if="isFieldGroupVisible('Membership')" class="ar-info-section">
              <div class="ar-section-header">
                <span class="ar-section-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7"/>
                    <path d="M2 7h20v5H2z"/>
                    <path d="M12 22V7"/>
                    <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z"/>
                    <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z"/>
                  </svg>
                </span>
                <div>
                  <h3 class="ar-section-title">Membership</h3>
                  <p class="ar-section-subtitle">Trial and promo status</p>
                </div>
              </div>
              <div class="ar-field-grid">
                <div v-if="isRunnerFieldVisible('trial-start')" class="ar-field">
                  <span class="ar-label">Trial start</span>
                  <span class="ar-value">{{ membershipValue(runner, 'startDate') }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('trial-end')" class="ar-field">
                  <span class="ar-label">Trial end</span>
                  <span class="ar-value">{{ membershipValue(runner, 'endDate') }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('promo-code')" class="ar-field">
                  <span class="ar-label">Promo code</span>
                  <span class="ar-value">{{ membershipValue(runner, 'promoCode') }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('promo-start')" class="ar-field">
                  <span class="ar-label">Promo start</span>
                  <span class="ar-value">{{ promoStartDate(runner) }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('promo-end')" class="ar-field">
                  <span class="ar-label">Promo end</span>
                  <span class="ar-value">{{ promoEndDate(runner) }}</span>
                </div>
              </div>
            </section>

            <section v-if="isFieldGroupVisible('Profile')" class="ar-info-section">
              <div class="ar-section-header">
                <span class="ar-section-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 2v20"/>
                    <path d="M5 7h14"/>
                    <path d="M7 7l-3 7h6L7 7z"/>
                    <path d="M17 7l-3 7h6l-3-7z"/>
                  </svg>
                </span>
                <div>
                  <h3 class="ar-section-title">Profile</h3>
                  <p class="ar-section-subtitle">Body metrics and public bio</p>
                </div>
              </div>
              <div class="ar-field-grid">
                <div v-if="isRunnerFieldVisible('height')" class="ar-field">
                  <span class="ar-label">Height</span>
                  <span class="ar-value">{{ displayValue(runner.height) }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('weight')" class="ar-field">
                  <span class="ar-label">Weight</span>
                  <span class="ar-value">{{ displayValue(runner.weight) }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('profile-text')" class="ar-field ar-field--wide">
                  <span class="ar-label">Profile text</span>
                  <span class="ar-value ar-value--multiline">{{ runnerProfileText(runner) }}</span>
                </div>
              </div>
            </section>

            <section v-if="isFieldGroupVisible('Run activity')" class="ar-info-section">
              <div class="ar-section-header">
                <span class="ar-section-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 17l6-6 4 4 8-8"/>
                    <path d="M14 7h7v7"/>
                  </svg>
                </span>
                <div>
                  <h3 class="ar-section-title">Run activity</h3>
                  <p class="ar-section-subtitle">Completion and recency snapshot</p>
                </div>
              </div>
              <div class="ar-field-grid">
                <div v-if="isRunnerFieldVisible('total-completed-runs')" class="ar-field">
                  <span class="ar-label">Total completed runs</span>
                  <span class="ar-value">{{ runnerReportValue(runner, 'totalCompletedRuns', '0') }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('last-liverun')" class="ar-field">
                  <span class="ar-label">Last LiveRun</span>
                  <span class="ar-value">{{ runnerReportValue(runner, 'lastLiveRun') }}</span>
                </div>
                <div v-if="isRunnerFieldVisible('days-since-last-liverun')" class="ar-field">
                  <span class="ar-label">Days since last LiveRun</span>
                  <span class="ar-value">{{ runnerReportValue(runner, 'daysSinceLastLiveRun', '0') }}</span>
                </div>
              </div>
            </section>
          </div>
        </article>
      </div>

      <div v-else class="ar-table-list">
        <div class="ar-table-header">
          <span>Runner</span>
          <span>Account</span>
          <span>Membership</span>
          <span>Profile</span>
          <span>Activity</span>
          <span>Actions</span>
        </div>
        <article
          v-for="(runner, index) in runners"
          :key="runner.id || index"
          class="ar-table-row ar-clickable-block"
          role="link"
          tabindex="0"
          :aria-label="'Open runner profile for ' + runnerName(runner)"
          @click="openRunnerProfile(runner)"
          @keydown.enter.prevent="openRunnerProfile(runner)"
          @keydown.space.prevent="openRunnerProfile(runner)"
        >
          <div class="ar-table-main">
            <div class="ar-table-identity">
              <span class="ar-table-avatar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="4"/>
                  <path d="M4 21a8 8 0 0 1 16 0"/>
                </svg>
              </span>
              <div class="ar-table-identity-copy">
                <h2 class="ar-table-name">{{ runnerName(runner) }}</h2>
                <div class="ar-table-meta">
                  <button class="ar-id-link" type="button" @click.stop="getRunnersData(runner.id)">ID {{ displayValue(runner.id) }}</button>
                  <template v-for="meta in runnerMetaFields(runner)">
                    <span :key="meta.id + '-table-dot'" class="ar-meta-dot"></span>
                    <span :key="meta.id + '-table-value'">{{ meta.value }}</span>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="isFieldGroupVisible('Account')" class="ar-table-zone">
              <span class="ar-table-zone-title">Account</span>
              <div class="ar-table-field-grid">
                <div v-for="field in compactAccountFields(runner)" :key="field.id" class="ar-table-field" :class="{ 'ar-table-field--wide': field.wide }">
                  <span class="ar-table-label">{{ field.label }}</span>
                  <span class="ar-table-value">{{ field.value }}</span>
                </div>
              </div>
            </div>

            <div v-if="isFieldGroupVisible('Membership')" class="ar-table-zone">
              <span class="ar-table-zone-title">Membership</span>
              <div class="ar-table-field-grid">
                <div v-for="field in compactMembershipFields(runner)" :key="field.id" class="ar-table-field">
                  <span class="ar-table-label">{{ field.label }}</span>
                  <span class="ar-table-value">{{ field.value }}</span>
                </div>
              </div>
            </div>

            <div v-if="isFieldGroupVisible('Profile')" class="ar-table-zone">
              <span class="ar-table-zone-title">Profile</span>
              <div class="ar-table-field-grid">
                <div v-for="field in compactProfileFields(runner)" :key="field.id" class="ar-table-field" :class="{ 'ar-table-field--wide': field.wide }">
                  <span class="ar-table-label">{{ field.label }}</span>
                  <span class="ar-table-value" :class="{ 'ar-table-value--clamp': field.wide }">{{ field.value }}</span>
                </div>
              </div>
            </div>

            <div v-if="isFieldGroupVisible('Run activity')" class="ar-table-zone">
              <span class="ar-table-zone-title">Activity</span>
              <div class="ar-table-field-grid">
                <div v-for="field in compactActivityFields(runner)" :key="field.id" class="ar-table-field">
                  <span class="ar-table-label">{{ field.label }}</span>
                  <span class="ar-table-value">{{ field.value }}</span>
                </div>
              </div>
            </div>

            <div class="ar-table-actions">
              <button class="ar-action-btn" type="button" @click.stop="getRunnersData(runner.id)" title="Open runner data">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 3h7v7"/>
                  <path d="M10 14L21 3"/>
                  <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/>
                </svg>
              </button>
              <button class="ar-action-btn" type="button" @click.stop="openPasswordModal(runner)" title="Change password">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </button>
              <button class="ar-action-btn" type="button" @click.stop="openBugseeSettingsModal(runner)" title="Feedback settings">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </button>
            </div>
          </div>
        </article>
      </div>

      <div v-if="!loading && totalPages > 0" class="ar-pagination-footer">
        <div class="ar-pagination-summary">
          Page {{ currentPage }} of {{ totalPages }}
        </div>
        <paginate
          :page-count="totalPages"
          :click-handler="handlePaginate"
          :initial-page="currentPage - 1"
          :force-page="currentPage - 1"
          :prev-text="'Prev'"
          :next-text="'Next'"
          :container-class="'ar-pagination'"
          :page-class="'ar-pagination-item'"
          :page-link-class="'ar-pagination-link'"
          :prev-class="'ar-pagination-item'"
          :prev-link-class="'ar-pagination-link'"
          :next-class="'ar-pagination-item'"
          :next-link-class="'ar-pagination-link'"
          :break-view-class="'ar-pagination-item'"
          :break-view-link-class="'ar-pagination-link'"
          :active-class="'ar-pagination-active'"
          :disabled-class="'ar-pagination-disabled'"
          ref="paginate">
        </paginate>
      </div>

      <password-management-modal
        :show="showPasswordModal"
        :runner-id="selectedRunnerId"
        :runner-name="selectedRunnerName"
        @close="closePasswordModal"
        @saved="onPasswordSaved"
      />

      <bugsee-settings-modal
        :show="showBugseeSettingsModal"
        :runner-id="selectedRunnerId"
        :runner-name="selectedRunnerName"
        @close="closeBugseeSettingsModal"
        @saved="onBugseeSettingsSaved"
      />

      <bugsee-filter-modal
        :show="showBugseeFilterModal"
        v-model="feedbackFilterEnabled"
        @close="showBugseeFilterModal = false"
        @apply="onBugseeFilterApply"
      />
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate'
import runnersService from '@/services/runnersService'
import PasswordManagementModal from './PasswordManagementModal'
import BugseeSettingsModal from './BugseeSettingsModal'
import BugseeFilterModal from './BugseeFilterModal'
import RunnerFieldVisibilityControl from './RunnerFieldVisibilityControl'
import {
  loadRunnerFieldPreference,
  resetRunnerFieldPreference,
  runnerFieldPreferencePages,
  saveRunnerFieldPreference
} from '@/utils/runnerFieldPreferences'

const fieldPreferenceConfig = runnerFieldPreferencePages.allRunners
const viewModeStorageKey = 'liverun.runnerView.v1.allRunners'
const defaultViewMode = 'cards'
const viewModes = ['cards', 'table']
const pageQueryKey = 'pages'
const searchQueryKey = 'search'

function isValidViewMode(value) {
  return viewModes.indexOf(value) !== -1
}

function loadRunnerViewMode() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultViewMode
  }
  try {
    const storedMode = window.localStorage.getItem(viewModeStorageKey)
    return isValidViewMode(storedMode) ? storedMode : defaultViewMode
  } catch (error) {
    return defaultViewMode
  }
}

function saveRunnerViewMode(mode) {
  if (typeof window === 'undefined' || !window.localStorage || !isValidViewMode(mode)) {
    return
  }
  try {
    window.localStorage.setItem(viewModeStorageKey, mode)
  } catch (error) {
    console.warn('Unable to save runner view mode preference:', error)
  }
}

function isNavigationDuplicate(error) {
  if (!error) {
    return false
  }
  return error.name === 'NavigationDuplicated' || error._name === 'NavigationDuplicated'
}

export default {
  name: 'AllRunners',
  data: () => {
    return {
      search: '',
      searchValue: '',
      searching: false,
      currentPage: 1,
      loading: false,
      showPasswordModal: false,
      showBugseeSettingsModal: false,
      showBugseeFilterModal: false,
      feedbackFilterEnabled: false,
      viewMode: loadRunnerViewMode(),
      selectedFieldIds: loadRunnerFieldPreference(fieldPreferenceConfig.storageKey, fieldPreferenceConfig.fields),
      selectedRunnerId: null,
      selectedRunnerName: '',
      AllRunners: {
        count: 0,
        pagination: {
          totalPages: 0,
          nextPage: null,
          prevPage: null
        },
        runners: []
      }
    }
  },
  computed: {
    runners () {
      if (!this.AllRunners || !Array.isArray(this.AllRunners.runners)) {
        return []
      }
      return this.AllRunners.runners
    },
    totalPages () {
      if (!this.AllRunners || !this.AllRunners.pagination) {
        return 0
      }
      return this.AllRunners.pagination.totalPages || 0
    },
    resultSummary () {
      const count = this.AllRunners && this.AllRunners.count ? this.AllRunners.count : 0
      if (this.searching && this.searchValue) {
        return count + ' results for "' + this.searchValue + '"'
      }
      return count + ' results'
    },
    fieldOptions () {
      return fieldPreferenceConfig.fields
    },
    trainer () {
      return this.$store.getters['Trainer/trainer']
    }
  },
  async mounted() {
    this.currentPage = this.normalizedPageFromRoute()
    this.applySearchFromRoute()
    await this.normalizePageQueryIfNeeded()
    await this.loadRunners()
  },
  methods: {
    emptyRunners () {
      return {
        count: 0,
        pagination: { totalPages: 0 },
        runners: []
      }
    },

    normalizedPageFromValue(value) {
      if (Array.isArray(value)) {
        value = value.length ? value[0] : ''
      }
      if (value === null || value === undefined || value === '') {
        return 1
      }
      var stringValue = String(value).trim()
      if (!/^\d+$/.test(stringValue)) {
        return 1
      }
      var page = parseInt(stringValue, 10)
      if (!page || page < 1) {
        return 1
      }
      return page
    },

    normalizedPageFromQuery(query) {
      return this.normalizedPageFromValue(query && query[pageQueryKey])
    },

    normalizedPageFromRoute() {
      return this.normalizedPageFromQuery(this.$route.query)
    },

    normalizedSearchFromValue(value) {
      if (Array.isArray(value)) {
        value = value.length ? value[0] : ''
      }
      if (value === null || value === undefined) {
        return ''
      }
      return String(value).trim()
    },

    normalizedSearchFromQuery(query) {
      return this.normalizedSearchFromValue(query && query[searchQueryKey])
    },

    normalizedSearchFromRoute() {
      return this.normalizedSearchFromQuery(this.$route.query)
    },

    applySearchFromRoute() {
      var routeSearch = this.normalizedSearchFromRoute()
      this.search = routeSearch
      this.searchValue = routeSearch
      this.searching = !!routeSearch
    },

    routePageNeedsNormalization(query) {
      var sourceQuery = query || this.$route.query
      var rawPage = sourceQuery[pageQueryKey]
      if (rawPage === null || rawPage === undefined || rawPage === '') {
        return false
      }
      if (Array.isArray(rawPage)) {
        rawPage = rawPage.length ? rawPage[0] : ''
      }
      return String(rawPage).trim() !== String(this.normalizedPageFromValue(rawPage))
    },

    async normalizePageQueryIfNeeded() {
      if (!this.routePageNeedsNormalization()) {
        return
      }
      await this.syncPageQuery(this.normalizedPageFromRoute(), 'replace')
    },

    async syncPageQuery(page, navigationMode) {
      var normalizedPage = this.normalizedPageFromValue(page)
      var normalizedSearch = this.normalizedSearchFromValue(this.searchValue)
      var currentQuery = this.$route.query || {}
      var currentSearch = this.normalizedSearchFromQuery(currentQuery)
      if (String(currentQuery[pageQueryKey]) === String(normalizedPage) && currentSearch === normalizedSearch) {
        return
      }

      var query = Object.assign({}, currentQuery)
      query[pageQueryKey] = String(normalizedPage)
      if (normalizedSearch) {
        query[searchQueryKey] = normalizedSearch
      } else {
        delete query[searchQueryKey]
      }
      var method = navigationMode === 'push' ? 'push' : 'replace'

      try {
        await this.$router[method]({ query: query })
      } catch (error) {
        if (!isNavigationDuplicate(error)) {
          throw error
        }
      }
    },

    syncPaginateSelection() {
      this.$nextTick(function () {
        if (this.$refs.paginate) {
          this.$refs.paginate.selected = this.currentPage - 1
        }
      })
    },

    async setPageFromControl(page, navigationMode) {
      var normalizedPage = this.normalizedPageFromValue(page)
      this.currentPage = normalizedPage
      await this.syncPageQuery(normalizedPage, navigationMode)
      this.syncPaginateSelection()
      await this.loadRunners()
    },

    async clampCurrentPageIfNeeded() {
      var targetPage = this.currentPage
      if (this.totalPages > 0 && this.currentPage > this.totalPages) {
        targetPage = this.totalPages
      } else if (this.totalPages === 0 && this.currentPage !== 1) {
        targetPage = 1
      }

      if (targetPage === this.currentPage) {
        return
      }

      this.currentPage = targetPage
      await this.syncPageQuery(targetPage, 'replace')
      this.syncPaginateSelection()
      await this.loadRunners()
    },

    async loadRunners() {
      try {
        this.loading = true
        console.log('Loading runners with params:', {
          page: this.currentPage,
          limit: 50,
          search: this.searchValue
        })

        const response = await runnersService.getAllRunners({
          page: this.currentPage,
          limit: 50,
          search: this.searchValue,
          feedback_enabled: this.feedbackFilterEnabled
        })

        console.log('API response:', response)

        if (response.body && response.body.success && response.body.data && response.body.data.AllRunners) {
          this.AllRunners = response.body.data.AllRunners
          console.log('Successfully loaded runners:', this.AllRunners)
        } else {
          console.error('Unexpected API response format:', response)
          this.AllRunners = this.emptyRunners()
        }
      } catch (error) {
        console.error('Error loading runners:', error)
        this.AllRunners = this.emptyRunners()
      } finally {
        this.loading = false
      }

      this.syncPaginateSelection()
      await this.clampCurrentPageIfNeeded()
    },

    async handlePaginate (page) {
      await this.setPageFromControl(page, 'push')
    },

    setViewMode(mode) {
      if (!isValidViewMode(mode) || this.viewMode === mode) {
        return
      }
      this.viewMode = mode
      saveRunnerViewMode(mode)
    },

    unicodeToChar(text) {
      if (text != null) {
        return String(text).replace(/\\u[\dA-F]{4}/gi, function (match) {
          return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
        })
      }
      return text
    },

    displayValue(value, fallback) {
      const emptyFallback = fallback || '-'
      if (value === 0) {
        return '0'
      }
      if (value === false) {
        return 'No'
      }
      if (value === true) {
        return 'Yes'
      }
      if (value === null || value === undefined) {
        return emptyFallback
      }
      const stringValue = String(value).trim()
      return stringValue ? stringValue : emptyFallback
    },

    runnerName(runner) {
      return this.displayValue(runner && runner.name, 'Unnamed runner')
    },

    runnerMetaFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('location')) {
        fields.push({ id: 'location', value: this.displayValue(runner && runner.location) })
      }
      if (this.isRunnerFieldVisible('gender')) {
        fields.push({ id: 'gender', value: this.displayValue(runner && runner.gender) })
      }
      return fields
    },

    isRunnerFieldVisible(id) {
      return this.selectedFieldIds.indexOf(id) !== -1
    },

    isFieldGroupVisible(group) {
      return this.fieldOptions.some(function (field) {
        return field.group === group && this.isRunnerFieldVisible(field.id)
      }, this)
    },

    applyFieldSelection(selectedIds) {
      this.selectedFieldIds = saveRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions, selectedIds)
    },

    resetFieldSelection() {
      this.selectedFieldIds = resetRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions)
    },

    runnerEmail(runner) {
      return this.displayValue(runner && runner.user && runner.user.email)
    },

    runnerAccountCreate(runner) {
      return this.displayValue(runner && runner.user && runner.user.accountCreate)
    },

    runnerProfileText(runner) {
      if (!runner || !runner.profileText) {
        return '-'
      }
      return this.displayValue(this.unicodeToChar(runner.profileText))
    },

    membershipValue(runner, key) {
      if (!runner || !runner.membership) {
        return '-'
      }
      return this.displayValue(runner.membership[key])
    },

    hasPromoCode(runner) {
      return !!(runner && runner.membership && runner.membership.promoCode)
    },

    promoStartDate(runner) {
      if (!this.hasPromoCode(runner)) {
        return '-'
      }
      if (runner.membership && runner.membership.promoStartDate) {
        return this.displayValue(runner.membership.promoStartDate)
      }
      if (runner.promo_report && runner.promo_report.startDate) {
        return this.displayValue(runner.promo_report.startDate)
      }
      return '-'
    },

    promoEndDate(runner) {
      if (!this.hasPromoCode(runner)) {
        return '-'
      }
      if (runner.membership && runner.membership.promoEndDate) {
        return this.displayValue(runner.membership.promoEndDate)
      }
      if (runner.membership && runner.membership.expirationDate) {
        return this.displayValue(runner.membership.expirationDate)
      }
      if (runner.promo_report && runner.promo_report.endDate) {
        return this.displayValue(runner.promo_report.endDate)
      }
      return '-'
    },

    runnerReportValue(runner, key, fallback) {
      if (!runner || !runner.runner_report) {
        return fallback || '-'
      }
      return this.displayValue(runner.runner_report[key], fallback)
    },

    compactAccountFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('email')) {
        fields.push({ id: 'email', label: 'Email', value: this.runnerEmail(runner), wide: true })
      }
      if (this.isRunnerFieldVisible('account-create')) {
        fields.push({ id: 'account-create', label: 'Created', value: this.runnerAccountCreate(runner) })
      }
      if (this.isRunnerFieldVisible('facebook')) {
        fields.push({ id: 'facebook', label: 'Facebook', value: runner && runner.facebook ? 'Yes' : 'No' })
      }
      if (this.isRunnerFieldVisible('website')) {
        fields.push({ id: 'website', label: 'Website', value: this.displayValue(runner && runner.website), wide: true })
      }
      return fields
    },

    compactMembershipFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('trial-start')) {
        fields.push({ id: 'trial-start', label: 'Trial start', value: this.membershipValue(runner, 'startDate') })
      }
      if (this.isRunnerFieldVisible('trial-end')) {
        fields.push({ id: 'trial-end', label: 'Trial end', value: this.membershipValue(runner, 'endDate') })
      }
      if (this.isRunnerFieldVisible('promo-code')) {
        fields.push({ id: 'promo-code', label: 'Promo', value: this.membershipValue(runner, 'promoCode') })
      }
      if (this.isRunnerFieldVisible('promo-start')) {
        fields.push({ id: 'promo-start', label: 'Promo start', value: this.promoStartDate(runner) })
      }
      if (this.isRunnerFieldVisible('promo-end')) {
        fields.push({ id: 'promo-end', label: 'Promo end', value: this.promoEndDate(runner) })
      }
      return fields
    },

    compactProfileFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('height')) {
        fields.push({ id: 'height', label: 'Height', value: this.displayValue(runner && runner.height) })
      }
      if (this.isRunnerFieldVisible('weight')) {
        fields.push({ id: 'weight', label: 'Weight', value: this.displayValue(runner && runner.weight) })
      }
      if (this.isRunnerFieldVisible('profile-text')) {
        fields.push({ id: 'profile-text', label: 'Profile', value: this.runnerProfileText(runner), wide: true })
      }
      return fields
    },

    compactActivityFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('total-completed-runs')) {
        fields.push({ id: 'total-completed-runs', label: 'Completed', value: this.runnerReportValue(runner, 'totalCompletedRuns', '0') })
      }
      if (this.isRunnerFieldVisible('last-liverun')) {
        fields.push({ id: 'last-liverun', label: 'Last LiveRun', value: this.runnerReportValue(runner, 'lastLiveRun') })
      }
      if (this.isRunnerFieldVisible('days-since-last-liverun')) {
        fields.push({ id: 'days-since-last-liverun', label: 'Days since', value: this.runnerReportValue(runner, 'daysSinceLastLiveRun', '0') })
      }
      return fields
    },

    openRunnerProfile(runner) {
      if (!runner || !runner.id) return
      this.getRunnersData(runner.id)
    },

    getRunnersData(id){
      if (!id) return
      let route = this.$router.resolve({
        name: 'runner-data',
        params: {
          runnerId: id,
          raceId: 2285
        }
      })
      window.open(route.href, '_blank')
    },

    async handleSearch () {
      this.searchValue = this.search ? this.search.trim() : ''
      this.searching = !!this.searchValue
      await this.setPageFromControl(1, 'replace')
    },

    openPasswordModal(runner) {
      if (!runner || !runner.id) return
      this.selectedRunnerId = runner.id
      this.selectedRunnerName = runner.name || 'Unknown'
      this.showPasswordModal = true
    },

    closePasswordModal() {
      this.showPasswordModal = false
      this.selectedRunnerId = null
      this.selectedRunnerName = ''
    },

    onPasswordSaved() {
      console.log('Password updated successfully')
    },

    openBugseeSettingsModal(runner) {
      if (!runner || !runner.id) return
      this.selectedRunnerId = runner.id
      this.selectedRunnerName = runner.name || 'Unknown'
      this.showBugseeSettingsModal = true
    },

    closeBugseeSettingsModal() {
      this.showBugseeSettingsModal = false
      this.selectedRunnerId = null
      this.selectedRunnerName = ''
    },

    onBugseeSettingsSaved() {
      this.loadRunners()
    },

    async onBugseeFilterApply() {
      await this.setPageFromControl(1, 'replace')
    }
  },
  watch: {
    '$route.query': async function (query, oldQuery) {
      var routePage = this.normalizedPageFromQuery(query)
      var routeSearch = this.normalizedSearchFromQuery(query)
      var oldPage = this.normalizedPageFromQuery(oldQuery)
      var oldSearch = this.normalizedSearchFromQuery(oldQuery)

      if (this.routePageNeedsNormalization(query)) {
        this.search = routeSearch
        this.searchValue = routeSearch
        this.searching = !!routeSearch
        await this.syncPageQuery(routePage, 'replace')
        return
      }

      if (routePage === oldPage && routeSearch === oldSearch) {
        this.syncPaginateSelection()
        return
      }

      if (routePage === this.currentPage && routeSearch === this.searchValue) {
        this.syncPaginateSelection()
        return
      }

      this.currentPage = routePage
      this.search = routeSearch
      this.searchValue = routeSearch
      this.searching = !!routeSearch
      this.syncPaginateSelection()
      await this.loadRunners()
    }
  },
  components: {
    Paginate,
    PasswordManagementModal,
    BugseeSettingsModal,
    BugseeFilterModal,
    RunnerFieldVisibilityControl
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.ar-page {
  font-family: 'Inter', sans-serif;
  color: #1a1a2e;
}

.ar-toolbar,
.ar-card,
.ar-empty,
.ar-pagination-footer {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.ar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.85rem;
}

.ar-toolbar-left,
.ar-search-row,
.ar-runner-meta,
.ar-actions,
.ar-card-header,
.ar-section-header,
.ar-view-switcher,
.ar-view-option,
.ar-table-identity,
.ar-table-meta,
.ar-table-actions {
  display: flex;
  align-items: center;
}

.ar-toolbar-left {
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.ar-search-panel {
  flex: 1;
  min-width: 0;
}

.ar-search-row {
  justify-content: flex-end;
  gap: 10px;
}

.ar-search-input {
  width: 100%;
  max-width: 520px;
  height: 36px;
  border: 1.5px solid #e0e0ea;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  padding: 0 12px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.ar-search-input:focus {
  border-color: #5b4fcf;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.ar-filter-btn,
.ar-primary-btn,
.ar-id-link,
.ar-action-btn,
.ar-view-option {
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.ar-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 33px;
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  border-radius: 7px;
  padding: 0 13px;
  font-size: 12.5px;
  font-weight: 600;
}

.ar-filter-btn:hover {
  background: #f0effe;
}

.ar-view-switcher {
  height: 33px;
  padding: 3px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  gap: 3px;
}

.ar-view-option {
  justify-content: center;
  gap: 6px;
  height: 25px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 700;
  padding: 0 9px;
}

.ar-view-option:hover {
  color: #5b4fcf;
  background: #f0effe;
}

.ar-view-option:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.12);
}

.ar-view-option--active,
.ar-view-option--active:disabled {
  border-color: #5b4fcf;
  background: #5b4fcf;
  color: #fff;
  cursor: default;
}

.ar-filter-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b4fcf;
}

.ar-filter-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  border-radius: 20px;
  background: #f0effe;
  color: #413b94;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  padding: 2px 8px;
  text-transform: uppercase;
}

.ar-primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #5b4fcf;
  color: #fff;
  padding: 0 18px;
  font-size: 13px;
  font-weight: 600;
}

.ar-primary-btn:hover {
  background: #4338ca;
  box-shadow: 0 4px 16px rgba(91, 79, 207, 0.35);
  transform: translateY(-1px);
}

.ar-btn-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.ar-result-summary {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
  margin-top: 5px;
  text-align: right;
}

.ar-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ar-table-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.ar-table-header {
  display: grid;
  grid-template-columns: minmax(180px, 1.1fr) minmax(180px, 1.15fr) minmax(210px, 1.25fr) minmax(170px, 1fr) minmax(160px, 0.95fr) 110px;
  gap: 10px;
  padding: 0 1.25rem 0.3rem;
  color: #8a8a9a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.ar-table-row {
  min-height: 104px;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  padding: 0.7rem 1.25rem;
}

.ar-table-main {
  display: grid;
  grid-template-columns: minmax(180px, 1.1fr) minmax(180px, 1.15fr) minmax(210px, 1.25fr) minmax(170px, 1fr) minmax(160px, 0.95fr) 110px;
  gap: 10px;
  min-height: 82px;
  align-items: stretch;
}

.ar-table-identity {
  min-width: 0;
  gap: 10px;
}

.ar-table-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
}

.ar-table-avatar svg {
  width: 15px;
  height: 15px;
}

.ar-table-identity-copy {
  min-width: 0;
}

.ar-table-name {
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ar-table-meta {
  flex-wrap: wrap;
  gap: 5px;
  color: #8a8a9a;
  font-size: 11px;
  line-height: 1.35;
  margin-top: 5px;
}

.ar-table-zone {
  min-width: 0;
  border: 1px solid #f0f0f5;
  border-radius: 10px;
  background: #fafafa;
  padding: 8px;
}

.ar-table-zone-title {
  display: block;
  color: #8a8a9a;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.ar-table-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 8px;
}

.ar-table-field {
  min-width: 0;
}

.ar-table-field--wide {
  grid-column: 1 / -1;
}

.ar-table-label,
.ar-table-value {
  display: block;
}

.ar-table-label {
  color: #8a8a9a;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.ar-table-value {
  color: #1a1a2e;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.ar-table-value--clamp {
  max-height: 29px;
  overflow: hidden;
}

.ar-table-actions {
  justify-content: flex-end;
  gap: 7px;
  min-width: 0;
}

.ar-card {
  padding: 1rem 1.25rem;
}

.ar-clickable-block {
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.ar-clickable-block:hover,
.ar-clickable-block:focus {
  border-color: #d8d5fb;
  box-shadow: 0 6px 18px rgba(91, 79, 207, 0.12);
  outline: none;
  transform: translateY(-1px);
}

.ar-card-header {
  gap: 12px;
  justify-content: space-between;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid #f0f0f5;
}

.ar-card-icon,
.ar-section-icon,
.ar-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f0effe;
  color: #5b4fcf;
}

.ar-card-icon,
.ar-empty-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.ar-card-icon svg,
.ar-section-icon svg,
.ar-empty-icon svg,
.ar-action-btn svg {
  width: 16px;
  height: 16px;
}

.ar-runner-title {
  flex: 1;
  min-width: 0;
}

.ar-runner-name {
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.ar-runner-meta {
  flex-wrap: wrap;
  gap: 6px;
  color: #8a8a9a;
  font-size: 12px;
  margin-top: 3px;
}

.ar-id-link {
  border: none;
  background: transparent;
  color: #4338ca;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
}

.ar-id-link:hover {
  color: #5b4fcf;
}

.ar-meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d8d8e6;
}

.ar-actions {
  gap: 8px;
  margin-left: auto;
}

.ar-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e8e8ed;
  border-radius: 50%;
  background: #fff;
  color: #5b4fcf;
}

.ar-action-btn:hover {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.ar-section-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1.2fr) minmax(0, 1.1fr) minmax(0, 0.95fr);
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.ar-info-section {
  min-width: 0;
  border: 1px solid #f0f0f5;
  border-radius: 10px;
  background: #fff;
  padding: 0.75rem;
}

.ar-section-header {
  gap: 8px;
  margin-bottom: 8px;
}

.ar-section-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
}

.ar-section-title {
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.ar-section-subtitle {
  color: #8a8a9a;
  font-size: 10.5px;
  margin: 2px 0 0;
}

.ar-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.ar-field {
  min-width: 0;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  background: #fafafa;
  padding: 7px 9px;
}

.ar-field--wide {
  grid-column: 1 / -1;
}

.ar-label,
.ar-value {
  display: block;
}

.ar-label {
  color: #8a8a9a;
  font-size: 9.5px;
  font-weight: 700;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.ar-value {
  color: #1a1a2e;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
}

.ar-value--break {
  overflow-wrap: anywhere;
}

.ar-value--multiline {
  color: #4d4d61;
  font-weight: 500;
  max-height: 48px;
  overflow: hidden;
  white-space: pre-wrap;
}

.ar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 2rem;
  text-align: center;
}

.ar-empty-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  margin: 14px 0 5px;
}

.ar-empty-text {
  color: #8a8a9a;
  font-size: 13px;
  margin: 0;
}

.ar-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.85rem;
  padding: 0.85rem 1.1rem;
}

.ar-pagination-summary {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
}

.ar-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.ar-pagination-item {
  display: inline-flex;
}

.ar-pagination-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  color: #5b4fcf;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  text-decoration: none;
  cursor: pointer;
}

.ar-pagination-link:hover {
  background: #f0effe;
  border-color: #5b4fcf;
}

.ar-pagination-active .ar-pagination-link {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.ar-pagination-disabled .ar-pagination-link {
  color: #c8c8d8;
  cursor: default;
  background: #fafafa;
  border-color: #e8e8ed;
}

.ar-card--skeleton {
  pointer-events: none;
}

.ar-table-list--skeleton {
  pointer-events: none;
}

.ar-skeleton-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.ar-skeleton-heading {
  flex: 1;
  min-width: 0;
}

.ar-skeleton-title {
  width: 210px;
  height: 16px;
}

.ar-skeleton-subtitle {
  width: 320px;
  height: 11px;
  margin-top: 8px;
}

.ar-skeleton-actions {
  display: flex;
  gap: 8px;
}

.ar-skeleton-action {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.ar-table-skeleton-title {
  width: 120px;
  height: 15px;
}

.ar-table-skeleton-meta {
  width: 150px;
  height: 10px;
  margin-top: 8px;
}

.ar-table-skeleton-label {
  width: 58px;
  height: 9px;
}

.ar-table-skeleton-value {
  width: 100%;
  height: 12px;
  margin-top: 8px;
}

.ar-table-skeleton-value--short {
  width: 68%;
  margin-top: 6px;
}

.ar-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 1rem;
}

.ar-skeleton-field {
  height: 54px;
}

@media (max-width: 900px) {
  .ar-toolbar,
  .ar-pagination-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .ar-toolbar-left {
    width: 100%;
  }

  .ar-search-row {
    justify-content: stretch;
  }

  .ar-search-input {
    width: 100%;
  }

  .ar-result-summary {
    text-align: left;
  }

  .ar-section-grid,
  .ar-skeleton-grid {
    grid-template-columns: 1fr;
  }

  .ar-card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .ar-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .ar-table-header {
    display: none;
  }

  .ar-table-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ar-table-identity,
  .ar-table-actions {
    grid-column: 1 / -1;
  }

  .ar-table-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 560px) {
  .ar-card,
  .ar-toolbar,
  .ar-empty {
    padding: 1rem;
  }

  .ar-field-grid {
    grid-template-columns: 1fr;
  }

  .ar-search-row {
    flex-direction: column;
  }

  .ar-view-switcher {
    width: 100%;
  }

  .ar-view-option {
    flex: 1;
  }

  .ar-table-row {
    padding: 1rem;
  }

  .ar-table-main,
  .ar-table-field-grid {
    grid-template-columns: 1fr;
  }

  .ar-primary-btn {
    width: 100%;
  }

  .pg-title {
    font-size: 30px;
  }
}
</style>



// WEBPACK FOOTER //
// All.vue?22fc3422